import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../utils/apihelper'

export const listFaceEmbeddings = createAsyncThunk(
  'faceEmbeddings/list',
  async ({ tenantId }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/api/v1/tenants/${tenantId}/face-embeddings/`)
      return Array.isArray(response.data) ? response.data : []
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch people')
    }
  }
)

export const getFaceEmbedding = createAsyncThunk(
  'faceEmbeddings/getById',
  async ({ tenantId, faceEmbeddingId }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/api/v1/tenants/${tenantId}/face-embeddings/${faceEmbeddingId}`)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch person')
    }
  }
)

export const enrollFace = createAsyncThunk(
  'faceEmbeddings/enroll',
  async ({ tenantId, formData }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/api/v1/tenants/${tenantId}/face-embeddings/enroll`,
        formData
      )
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to enroll person')
    }
  }
)

// PUT /{id} — update metadata only (name, employee_id, email, phone_number)
export const updateFaceEmbeddingMeta = createAsyncThunk(
  'faceEmbeddings/updateMeta',
  async ({ tenantId, faceEmbeddingId, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `/api/v1/tenants/${tenantId}/face-embeddings/${faceEmbeddingId}`,
        data
      )
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update person')
    }
  }
)

// PUT /{id}/photos — replace photos + re-run inference
export const updateFaceEmbeddingPhotos = createAsyncThunk(
  'faceEmbeddings/updatePhotos',
  async ({ tenantId, faceEmbeddingId, formData }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `/api/v1/tenants/${tenantId}/face-embeddings/${faceEmbeddingId}/photos`,
        formData
      )
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update photos')
    }
  }
)

export const deleteFaceEmbedding = createAsyncThunk(
  'faceEmbeddings/delete',
  async ({ tenantId, faceEmbeddingId }, { rejectWithValue }) => {
    try {
      await api.delete(`/api/v1/tenants/${tenantId}/face-embeddings/${faceEmbeddingId}`)
      return faceEmbeddingId
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete person')
    }
  }
)

const faceEmbeddingSlice = createSlice({
  name: 'faceEmbeddings',
  initialState: {
    people: [],
    selected: null,
    isLoading: false,
    error: null,
  },
  reducers: {
    clearError:    (state) => { state.error = null },
    clearSelected: (state) => { state.selected = null },
  },
  extraReducers: (builder) => {
    builder
      .addCase(listFaceEmbeddings.pending,    (state) => { state.isLoading = true; state.error = null })
      .addCase(listFaceEmbeddings.fulfilled,  (state, action) => { state.isLoading = false; state.people = action.payload })
      .addCase(listFaceEmbeddings.rejected,   (state, action) => { state.isLoading = false; state.error = action.payload })

      .addCase(getFaceEmbedding.pending,      (state) => { state.isLoading = true; state.error = null })
      .addCase(getFaceEmbedding.fulfilled,    (state, action) => { state.isLoading = false; state.selected = action.payload })
      .addCase(getFaceEmbedding.rejected,     (state, action) => { state.isLoading = false; state.error = action.payload })

      .addCase(enrollFace.pending,            (state) => { state.isLoading = true; state.error = null })
      .addCase(enrollFace.fulfilled,          (state) => { state.isLoading = false })
      .addCase(enrollFace.rejected,           (state, action) => { state.isLoading = false; state.error = action.payload })

      .addCase(updateFaceEmbeddingMeta.pending,   (state) => { state.isLoading = true; state.error = null })
      .addCase(updateFaceEmbeddingMeta.fulfilled, (state, action) => {
        state.isLoading = false
        state.people = state.people.map((p) => p.id === action.payload.id ? { ...p, ...action.payload } : p)
      })
      .addCase(updateFaceEmbeddingMeta.rejected,  (state, action) => { state.isLoading = false; state.error = action.payload })

      .addCase(updateFaceEmbeddingPhotos.pending,   (state) => { state.isLoading = true; state.error = null })
      .addCase(updateFaceEmbeddingPhotos.fulfilled, (state) => { state.isLoading = false })
      .addCase(updateFaceEmbeddingPhotos.rejected,  (state, action) => { state.isLoading = false; state.error = action.payload })

      .addCase(deleteFaceEmbedding.pending,   (state) => { state.isLoading = true; state.error = null })
      .addCase(deleteFaceEmbedding.fulfilled, (state, action) => {
        state.isLoading = false
        state.people = state.people.filter((p) => p.id !== action.payload)
      })
      .addCase(deleteFaceEmbedding.rejected,  (state, action) => { state.isLoading = false; state.error = action.payload })
  },
})

export const { clearError, clearSelected } = faceEmbeddingSlice.actions
export default faceEmbeddingSlice.reducer
